import { useState, useEffect } from "react";
import { X, ChevronDown, ClipboardList, Wrench, CreditCard, HelpCircle, ExternalLink } from "lucide-react";
import { useLanguage } from "../contexts/LanguageContext";
import api from "../lib/api";

const TELEGRAM_ICON = (
  <svg viewBox="0 0 24 24" className="w-6 h-6" fill="currentColor">
    <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/>
  </svg>
);

const CATEGORIES = [
  { id: "order", icon: ClipboardList, titleTr: "Sipariş Desteği", titleEn: "Order Support", descTr: "Sipariş, teslimat ve panel akışı ile ilgili", descEn: "Order, delivery and panel flow", msgTr: "Merhaba, sipariş desteği istiyorum.", msgEn: "Hello, I need order support." },
  { id: "technical", icon: Wrench, titleTr: "Teknik Destek", titleEn: "Technical Support", descTr: "Hata, performans veya erişim problemi", descEn: "Error, performance or access issue", msgTr: "Merhaba, teknik destek istiyorum.", msgEn: "Hello, I need technical support." },
  { id: "payment", icon: CreditCard, titleTr: "Ödeme / Abonelik", titleEn: "Payment / Subscription", descTr: "Ödeme, abonelik, fatura soruları", descEn: "Payment, subscription, invoice questions", msgTr: "Merhaba, ödeme/abonelik konusunda destek istiyorum.", msgEn: "Hello, I need support with payment/subscription." },
  { id: "general", icon: HelpCircle, titleTr: "Genel Soru", titleEn: "General Question", descTr: "Diğer konular", descEn: "Other topics", msgTr: "Merhaba, genel bir sorum var.", msgEn: "Hello, I have a general question." }
];

export default function TelegramSupportWidget() {
  const { language } = useLanguage();
  const [open, setOpen] = useState(false);
  const [usernames, setUsernames] = useState([]);
  const [selectedUsername, setSelectedUsername] = useState("");
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    api.get("/support/info")
      .then((res) => {
        if (cancelled) return;
        const list = (res.data?.telegram_usernames || []).filter(Boolean);
        setUsernames(list);
        if (list.length > 0) {
          setSelectedUsername((prev) => prev || list[0]);
        }
      })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (usernames.length > 0 && !usernames.includes(selectedUsername)) setSelectedUsername(usernames[0]);
  }, [usernames, selectedUsername]);

  const handleCategoryClick = (cat) => {
    const msg = language === "tr" ? cat.msgTr : cat.msgEn;
    const page = typeof window !== "undefined" ? `\nSayfa: ${window.location.href}` : "";
    const text = encodeURIComponent(msg + page);
    const user = (selectedUsername || usernames[0] || "").replace(/^@/, "");
    if (user) window.open(`https://t.me/${user}?text=${text}`, "_blank");
  };

  if (loading || usernames.length === 0) return null;

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="fixed bottom-6 right-6 z-50 w-14 h-14 rounded-xl bg-[#0088cc] hover:bg-[#007ab8] text-white shadow-lg flex items-center justify-center transition-all hover:scale-105"
        aria-label={language === "tr" ? "Telegram Destek" : "Telegram Support"}
      >
        {TELEGRAM_ICON}
      </button>

      {open && (
        <div className="fixed inset-0 z-[100] bg-black/60" onClick={() => setOpen(false)}>
          <div className="fixed bottom-24 right-6 left-6 sm:left-auto sm:w-[28rem] max-h-[calc(100vh-8rem)] flex flex-col glass border border-white/10 rounded-lg shadow-xl overflow-hidden z-[101]" onClick={(e) => e.stopPropagation()}>
            <div className="p-4 border-b border-white/10 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="text-primary">{TELEGRAM_ICON}</span>
                <h2 className="font-mono font-bold text-lg">{language === "tr" ? "Telegram Destek" : "Telegram Support"}</h2>
              </div>
              <button type="button" onClick={() => setOpen(false)} className="p-1 hover:bg-white/10 rounded">
                <X className="w-5 h-5" />
              </button>
            </div>
            <p className="text-xs text-muted-foreground px-4 pt-1 flex-shrink-0">{language === "tr" ? "Mesaj Seç" : "Select Message"}</p>

            <div className="p-4 space-y-3 overflow-y-auto flex-1 min-h-0">
              <div>
                <label className="text-xs font-mono text-muted-foreground block mb-1">{language === "tr" ? "İletişim" : "Contact"}</label>
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => setDropdownOpen(!dropdownOpen)}
                    className="w-full flex items-center justify-between gap-2 px-3 py-2 bg-white/5 border border-white/10 rounded font-mono text-sm"
                  >
                    <span>@{selectedUsername || usernames[0]}</span>
                    <ChevronDown className={`w-4 h-4 transition-transform ${dropdownOpen ? "rotate-180" : ""}`} />
                  </button>
                  {dropdownOpen && (
                    <div className="absolute top-full left-0 right-0 mt-1 py-1 bg-background border border-white/10 rounded shadow-lg z-10">
                      {usernames.map((u) => (
                        <button key={u} type="button" onClick={() => { setSelectedUsername(u); setDropdownOpen(false); }} className="w-full px-3 py-2 text-left font-mono text-sm hover:bg-white/10">
                          @{u}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {CATEGORIES.map((cat) => {
                const Icon = cat.icon;
                return (
                  <button
                    key={cat.id}
                    type="button"
                    onClick={() => handleCategoryClick(cat)}
                    className="w-full flex items-center gap-3 p-3 rounded-lg bg-white/5 border border-white/10 hover:border-primary/50 hover:bg-white/10 text-left transition-all"
                  >
                    <Icon className="w-5 h-5 text-primary flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="font-mono font-bold text-sm">{language === "tr" ? cat.titleTr : cat.titleEn}</div>
                      <div className="text-xs text-muted-foreground">{language === "tr" ? cat.descTr : cat.descEn}</div>
                    </div>
                    <ExternalLink className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                  </button>
                );
              })}
            </div>

            <p className="text-xs text-muted-foreground px-4 pb-4 pt-1">{language === "tr" ? "Telegram'a yönlendirileceksiniz." : "You will be redirected to Telegram."}</p>
          </div>
        </div>
      )}
    </>
  );
}
